import {inject, Injectable} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import {AttributeFulfillmentTypeDirectus, DirectusService} from '@frontend/directus';
import {
  AttributeWithScores,
  EvaluationCriteriaWithScores,
  EvaluationSubCriteriaView,
  ProviderScoreInfo,
  ProviderScoresControllerService,
  ProviderScoresResponse,
  SubCriteriaWithScores,
  TenderPhaseType,
} from '@frontend/shared-api';
import {SubCriteriaEvaluationService} from './sub-criteria-evaluation.service';
import {SubCriteriaItem} from '../model/sub-criteria.interfaces';
import {DebugLogger} from '../utils/debug-logger.util';
import {
  FieldCombination,
  ProviderScoreCalculationResult,
  ScoreCalculationContext,
  ScoreCalculationService,
  SubCriteriaAttributeMap,
} from './score-calculation';

export {ProviderScoreCalculationResult, SubCriteriaAttributeMap, FieldCombination};

@Injectable({
  providedIn: 'root',
})
export class ProviderScoreCalculationService {
  private readonly subCriteriaEvaluationService = inject(SubCriteriaEvaluationService);
  private readonly providerScoresService = inject(ProviderScoresControllerService);
  private readonly directusService = inject(DirectusService);
  private readonly scoreCalculationService = inject(ScoreCalculationService);

  async calculateProviderScores(
    projectId: number,
    phase: TenderPhaseType,
  ): Promise<ProviderScoreCalculationResult> {
    const context = await this.buildCalculationContext(projectId, phase);

    const fieldCombinations = this.scoreCalculationService.processScoreCalculations(context);

    const result = {
      fieldCombinations,
      subCriteriaByEc: context.subCriteriaByEc,
      attributesMap: context.attributesMap,
      fulfillmentTypes: context.fulfillmentTypes,
      allScores: context.allScores,
      scoreToAttributeMap: context.scoreToAttributeMap,
      requestedCriteria: context.requestedCriteria,
      importanceScoreWeights: context.importanceScoreWeights,
    };

    return result;
  }

  getFieldCombination(
    fieldCombinations: Map<string, FieldCombination>,
    scNumber: number,
    hrToolDirectusId: string,
  ): FieldCombination | undefined {
    return this.scoreCalculationService.getFieldCombination(
      fieldCombinations,
      scNumber,
      hrToolDirectusId,
    );
  }

  calculateProviderTotal(
    fieldCombinations: Map<string, FieldCombination>,
    subCriteria: SubCriteriaItem[],
    hrToolDirectusId: string,
    scDistributionByEcNumber: {
      id: number;
      percentage: number;
      directusId: string;
    }[],
  ): {total: number; maxPossibleScore: number} {
    return this.scoreCalculationService.calculateProviderTotal(
      fieldCombinations,
      subCriteria,
      hrToolDirectusId,
      scDistributionByEcNumber,
    );
  }

  async calculateProviderTotalScores(
    projectId: number,
    phase: TenderPhaseType,
  ): Promise<Map<string, number>> {
    try {
      const calculationResult = await this.calculateProviderScores(projectId, phase);

      const scoresMap = new Map<string, number>();
      const allSubCriteria: SubCriteriaItem[] = [];
      calculationResult.subCriteriaByEc.forEach((ecSubCriteria) => {
        allSubCriteria.push(...ecSubCriteria);
      });

      const uniqueToolIds = new Set<string>();
      calculationResult.allScores.forEach((score) => {
        uniqueToolIds.add(score.hrToolDirectusId);
      });

      uniqueToolIds.forEach((toolId) => {
        const totalValues = this.calculateProviderTotal(
          calculationResult.fieldCombinations,
          allSubCriteria,
          toolId,
          [],
        );
        scoresMap.set(toolId, totalValues.total);
      });

      return scoresMap;
    } catch (error) {
      DebugLogger.error(
        '[ProviderScoreCalculation] Error calculating provider total scores:',
        error,
      );
      return new Map();
    }
  }

  private async buildCalculationContext(
    projectId: number,
    phase: TenderPhaseType,
  ): Promise<ScoreCalculationContext> {
    const [consolidatedResponse, fulfillmentTypes] = await Promise.all([
      firstValueFrom(
        this.providerScoresService.getProviderScoresByPhase({
          projectId,
          phase,
        }),
      ),
      this.loadFulfillmentTypes(),
    ]);

    const scoresData = this.extractScoresFromResponse(consolidatedResponse);

    const allCriteria = consolidatedResponse.evaluationCriteria;
    const importanceScoreWeights = this.extractImportanceWeightsFromResponse(consolidatedResponse);

    const {scores: allScores, scoreToAttributeMap} = scoresData;
    const requestedCriteria = allCriteria;

    const subCriteriaByEc = new Map<number, SubCriteriaItem[]>();
    const allSubCriteriaItems: SubCriteriaItem[] = [];

    for (const criteria of requestedCriteria) {
      const subCriteriaItems = await this.loadSubCriteriaForCriteria(projectId, criteria);
      subCriteriaByEc.set(criteria.ecNumber, subCriteriaItems);
      allSubCriteriaItems.push(...subCriteriaItems);
    }

    const attributesMap = await this.loadSubCriteriaAttributes(allSubCriteriaItems);

    const attributeImportanceScores =
      this.scoreCalculationService.extractImportanceScoresFromCriteria(requestedCriteria);

    return {
      projectId,
      ecNumbers: requestedCriteria.map((c) => c.ecNumber),
      phase,
      allScores,
      requestedCriteria,
      subCriteriaByEc,
      allSubCriteriaItems,
      attributesMap,
      fulfillmentTypes,
      scoreToAttributeMap,
      attributeImportanceScores,
      importanceScoreWeights,
    };
  }

  private async loadFulfillmentTypes(): Promise<AttributeFulfillmentTypeDirectus[]> {
    return await this.directusService.loadAttributeFulfillmentTypes();
  }

  private async loadSubCriteriaForCriteria(
    projectId: number,
    criteria: EvaluationCriteriaWithScores,
  ) {
    const subCriteriaItems = await firstValueFrom(
      this.subCriteriaEvaluationService.loadSubCriteriaForEvaluation(projectId, [
        {
          evaluationCriteriaId: criteria.id,
          criteriaData: {
            subCriteria: this.convertSubCriteriaForService(criteria.subCriteria),
            directusId: criteria.directusId,
          },
          ecNumber: criteria.ecNumber,
        },
      ]),
    );
    return subCriteriaItems[criteria.id] || [];
  }

  private convertSubCriteriaForService(
    subCriteria: SubCriteriaWithScores[],
  ): EvaluationSubCriteriaView[] {
    return subCriteria.map((sub) => ({
      id: sub.id,
      scNumber: sub.scNumber,
      included: sub.included,
      evaluationCriteriaId: sub.evaluationCriteriaId,
      directusId: sub.directusId,
      attributes: sub.attributes.map((attr) => ({
        id: attr.id,
        scaNumber: attr.scaNumber,
        directusId: attr.directusId,
        evaluationSubCriteriaId: sub.id,
        importanceScores: attr.importanceScores || [],
      })),
    }));
  }

  private async loadSubCriteriaAttributes(
    subCriteria: SubCriteriaItem[],
  ): Promise<SubCriteriaAttributeMap> {
    const attributeMap: SubCriteriaAttributeMap = {};

    for (const subCriterion of subCriteria) {
      attributeMap[subCriterion.directusId] =
        await this.directusService.loadSubCriteriaAttributesBySubCriteria(subCriterion.directusId);
    }

    return attributeMap;
  }

  private extractScoresFromResponse(response: ProviderScoresResponse): {
    scores: ProviderScoreInfo[];
    scoreToAttributeMap: Map<number, string>;
  } {
    const allScores: ProviderScoreInfo[] = [];
    const scoreToAttributeMap = new Map<number, string>();

    for (const criteria of response.evaluationCriteria) {
      for (const subCriterion of criteria.subCriteria) {
        this.processAttributeScores(subCriterion.attributes, allScores, scoreToAttributeMap);
      }
    }

    return {scores: allScores, scoreToAttributeMap};
  }

  private processAttributeScores(
    attributes: AttributeWithScores[],
    allScores: ProviderScoreInfo[],
    scoreToAttributeMap: Map<number, string>,
  ): void {
    for (const attribute of attributes) {
      for (const score of attribute.scores || []) {
        allScores.push(score);
        scoreToAttributeMap.set(score.id, attribute.directusId);
      }
    }
  }

  private extractImportanceWeightsFromResponse(
    response: ProviderScoresResponse,
  ): Map<string, number> {
    const weightMap = new Map<string, number>();
    response.importanceScores.forEach((scoreInfo) => {
      weightMap.set(scoreInfo.importanceScore.toString(), scoreInfo.weight);
    });
    return weightMap;
  }
}
